import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SectionCard } from "@/components/shared/PageChrome";

const toLocal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");

// Staff-recorded brief facts on an enquiry: when the brief was received (never inferred from creation time) and its type.
export function BriefEvidenceCard({ enquiry, onSaved }) {
  const [at, setAt] = useState(toLocal(enquiry.brief_received_at));
  const [type, setType] = useState(enquiry.brief_type || "UNSET");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/operational/enquiries/${enquiry.id}/brief`, { brief_received_at: at ? new Date(at).toISOString() : null, brief_type: type === "UNSET" ? null : type, reason });
      toast.success("Brief evidence recorded"); setReason(""); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record brief evidence"); }
    finally { setBusy(false); }
  };
  return (
    <SectionCard title="Brief evidence" subtitle={`Recorded by staff for reporting. Created in Atlas: ${enquiry.created_at ? new Date(enquiry.created_at).toLocaleString("en-GB") : "—"} (system time, not the brief).`} testId="brief-evidence-card" bodyClassName="px-5 pb-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <div><Label>Brief received (UTC)</Label><Input data-testid="brief-received-input" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></div>
        <div><Label>Brief type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="brief-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="UNSET">Not recorded</SelectItem><SelectItem value="ACCOMMODATION">Accommodation</SelectItem><SelectItem value="VENUE_FINDING">Venue-finding</SelectItem><SelectItem value="MIXED">Mixed</SelectItem></SelectContent>
          </Select></div>
        <div><Label>Reason (kept in audit)</Label><Input data-testid="brief-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. E-mail from LA received 09:12" /></div>
        <div className="flex items-end"><Button data-testid="brief-save-button" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" disabled={busy || reason.trim().length < 3} onClick={save}>Save</Button></div>
      </div>
      <p data-testid="brief-current" className="mt-2 text-[12px] text-jigsaw-muted">Current: {enquiry.brief_received_at ? new Date(enquiry.brief_received_at).toLocaleString("en-GB") : "not recorded"} · {enquiry.brief_type || "type not recorded"}</p>
    </SectionCard>
  );
}

const SECTORS = ["LOCAL_AUTHORITY", "HOUSING_ASSOCIATION", "INSURER", "NHS", "CORPORATE", "CHARITY", "OTHER"];

export function SectorCard({ client, onSaved }) {
  const [sector, setSector] = useState(client.sector || "UNSET");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/operational/organisations/${client.id}/sector`, { sector: sector === "UNSET" ? null : sector, reason });
      toast.success("Sector recorded"); setReason(""); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record sector"); }
    finally { setBusy(false); }
  };
  return (
    <SectionCard title="Commissioning sector" subtitle="Used to count invoiced HA / local-authority / insurer organisations. Unknown stays unknown - it is never guessed from the name." testId="sector-card" bodyClassName="px-5 pb-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Sector</Label>
          <Select value={sector} onValueChange={setSector}>
            <SelectTrigger data-testid="sector-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="UNSET">Not recorded</SelectItem>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Reason (kept in audit)</Label><Input data-testid="sector-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Contract type confirmed by client" /></div>
        <div className="flex items-end"><Button data-testid="sector-save-button" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" disabled={busy || reason.trim().length < 3} onClick={save}>Save</Button></div>
      </div>
      <p data-testid="sector-current" className="mt-2 text-[12px] text-jigsaw-muted">Current: {client.sector || "not recorded"}</p>
    </SectionCard>
  );
}
